/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { Button, CircularProgress } from "@mui/material";
import "./HeaderPanel.css";
import OriginColumn from "./OriginColumn";
import AddOriginColumn from "./AddOriginColumn";
import SideLabel from "./SideLabel";
import NitroService from "../../../utils/nitro.service";
import { useNitroStore } from "../../../store/Store";
import { useNitroCAStore } from "../../../store/CompetitiveAnalysisStore";
import {
  IFavoriteOrigin,
  IFavoriteOrigins,
} from "../../../types/IFavoriteOrigins";
import { icons } from "../../../svg";

// TODO: mode icons are not the same between screens
export type SelectedOrigin = { id: string; name: string };

const HeaderPanel = ({
  kochOrigins,
  competitorOrigins,
  user,
  setFavoriteOrigins,
}: {
  kochOrigins: IFavoriteOrigin[];
  competitorOrigins: IFavoriteOrigin[];
  user: string;
  setFavoriteOrigins: React.Dispatch<React.SetStateAction<IFavoriteOrigins[]>>;
}) => {
  const [
    product,
    setFetchFavoriteOrigins,
    currentFilters,
  ] = useNitroStore((state) => [
    state.product,
    state.setFetchFavoriteOrigins,
    state.currentFilters,
  ]);
  const [expandedDestinationId, setExpandedDestination, setOriginModeVisibility, triggerRefetchMultiLocation] = useNitroCAStore(
    (state) => [
      state.expandedDestId,
      state.setExpandedDestination,
      state.toggleOriginVisibility,
      state.triggerRefetchMultiLocation,
    ],
  );

  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [isAddingKochOrigin, setIsAddingKochOrigin] = useState(false);
  const [isAddingCompetitorOrigin, setIsAddingCompetitorOrigin] =
    useState(false);
  const [kochOriginIndex, setKochOriginIndex] = useState(0);
  const [competitorOriginIndex, setCompetitorOriginIndex] = useState(0);
  const [isLoadingNewKochOrigin, setIsLoadingNewKochOrigin] = useState(false);
  const [isLoadingNewCompetitorOrigin, setIsLoadingNewCompetitorOrigin] =
    useState(false);
  const [lastAddedOriginId, setLastAddedOriginId] = useState<string | null>(
    null,
  );
  const [addedOrigin, setAddedOrigin] = useState<{
    id: string;
    name: string;
    type: string;
  } | null>(null);
  const [removedOrigin, setRemovedOrigin] = useState<{
    id: string;
    type: string;
    timestamp: number;
  } | null>(null);
  const deletionTimestampRef = React.useRef<number>(0);
  const KOCH_ORIGINS_LIMIT = 5;
  const COMPETITOR_ORIGINS_LIMIT = 4;
  const isSearching =
    currentFilters.city || currentFilters.location || currentFilters.party;

  const allOriginsVisibilityKey = React.useMemo(() => {
    return [...kochOrigins, ...competitorOrigins]
      .map(
        (origin) =>
          `${origin.location.id}|${origin.competitor ? 1 : 0}|${
            origin.truckEnabled ? 1 : 0
          }|${origin.railEnabled ? 1 : 0}|${origin.truckMaxPrice ?? ""}|${
            origin.railMaxPrice ?? ""
          }`,
      )
      .sort()
      .join("||");
  }, [kochOrigins, competitorOrigins]);

  // handle adding an origin to the backend
  const handleAddOrigin = (type: string, selectedOrigin?: SelectedOrigin) => {
    if (!selectedOrigin) return;

    if (type === "koch") {
      setIsLoadingNewKochOrigin(true);
      setIsAddingKochOrigin(false);
    } else {
      setIsLoadingNewCompetitorOrigin(true);
      setIsAddingCompetitorOrigin(false);
    }

    setLastAddedOriginId(selectedOrigin.id);
    setAddedOrigin({ id: selectedOrigin.id, name: selectedOrigin.name, type });
  };

  useEffect(() => {
    if (!openMenuId) return;

    const handleClickOutside = () => {
      setOpenMenuId(null);
    };

    window.addEventListener("click", handleClickOutside);

    return () => {
      window.removeEventListener("click", handleClickOutside);
    };
  }, [openMenuId]);

  useEffect(() => {
    const addFavoriteOrigin = async () => {
      if (!addedOrigin) return;
      // when origin is added Truck is enabled by default, rail and barge disabled
      await NitroService.createFavoriteOrigin(
        user,
        addedOrigin.id,
        addedOrigin.name,
        product,
        addedOrigin.type !== "koch",
        true,
        false,
        false,
      );
      setAddedOrigin(null);
      setFetchFavoriteOrigins(true);
    };
    addFavoriteOrigin();
    triggerRefetchMultiLocation();
    if (expandedDestinationId) {
      setExpandedDestination({ id: expandedDestinationId, name: "" });
    }
  }, [
    addedOrigin,
    user,
    setFetchFavoriteOrigins,
    product,
    triggerRefetchMultiLocation,
  ]);

  useEffect(() => {
    if (!lastAddedOriginId) return;

    const isKochOriginAdded = kochOrigins.some(
      (origin) => origin.location.id === lastAddedOriginId,
    );
    const isCompetitorOriginAdded = competitorOrigins.some(
      (origin) => origin.location.id === lastAddedOriginId,
    );

    if (isLoadingNewKochOrigin && isKochOriginAdded) {
      setIsLoadingNewKochOrigin(false);
      setLastAddedOriginId(null);
    }

    if (isLoadingNewCompetitorOrigin && isCompetitorOriginAdded) {
      setIsLoadingNewCompetitorOrigin(false);
      setLastAddedOriginId(null);
    }
  }, [
    kochOrigins,
    competitorOrigins,
    lastAddedOriginId,
    isLoadingNewKochOrigin,
    isLoadingNewCompetitorOrigin,
  ]);

  useEffect(() => {
    setKochOriginIndex(0);
    setCompetitorOriginIndex(0);
  }, [product]);

  // Keep hidden-origins state in sync for all origins (not just paged/visible columns).
  // Without this, off-screen origins may never initialize visibility until paged into view.
  useEffect(() => {
    const allOrigins = [...kochOrigins, ...competitorOrigins];

    allOrigins.forEach((origin) => {
      const originKey = `${origin.competitor ? "competitor" : "koch"}-${
        origin.location.name
      }`;

      const isTruckVisible =
        (!origin.truckMaxPrice && !origin.railMaxPrice) || origin.truckEnabled;
      const isRailVisible = !!origin.railEnabled;

      setOriginModeVisibility(originKey, "truck", isTruckVisible);
      setOriginModeVisibility(originKey, "rail", isRailVisible);
    });
  }, [allOriginsVisibilityKey, setOriginModeVisibility]);

  const filterOutOriginById = (favorites: any[], originId: string) =>
    favorites.filter((fav) => fav.location.id !== originId);

  const handleRemoveOrigin = (type: string, originId: string) => {
    const currentDeletionTime = Date.now();
    deletionTimestampRef.current = currentDeletionTime;
    setFavoriteOrigins((currentFavoriteOrigins) =>
      currentFavoriteOrigins.map((originGroup) => {
        if (originGroup.product !== product) {
          return originGroup;
        }
        return {
          ...originGroup,
          favorites: filterOutOriginById(originGroup.favorites, originId),
        };
      }),
    );

    setRemovedOrigin({ id: originId, type, timestamp: currentDeletionTime });
  };

  useEffect(() => {
    const removeFavoriteOrigin = async () => {
      if (!removedOrigin) return;

      try {
        await NitroService.deleteFavoriteOrigin(
          user,
          product,
          removedOrigin.id,
          removedOrigin.type !== "koch",
        );

        if (deletionTimestampRef.current === removedOrigin.timestamp) {
          setFetchFavoriteOrigins(true);
        }
      } catch (error) {
        console.error("Error removing origin:", error);
        setFetchFavoriteOrigins(true);
      } finally {
        setRemovedOrigin(null);
      }
    };
    removeFavoriteOrigin();
    triggerRefetchMultiLocation();
    if (expandedDestinationId) {
      setExpandedDestination({ id: expandedDestinationId, name: "" });
    }
  }, [
    removedOrigin,
    user,
    product,
    setFetchFavoriteOrigins,
    triggerRefetchMultiLocation,
  ]);

  const visibleKochOrigins = kochOrigins.slice(
    kochOriginIndex,
    kochOriginIndex + KOCH_ORIGINS_LIMIT,
  );
  const visibleCompetitorOrigins = competitorOrigins.slice(
    competitorOriginIndex,
    competitorOriginIndex + COMPETITOR_ORIGINS_LIMIT,
  );
  return (
    <div className="header-panel-wrapper">
      <div className="header-panel-spacer" />
      <div
        className="header-panel"
        style={{
          height: `${
            kochOrigins.length === 0 &&
            competitorOrigins.length === 0 &&
            !isAddingKochOrigin &&
            !isAddingCompetitorOrigin
              ? "100px"
              : "auto"
          }`,
        }}
      >
        <div
          className="origins-panel"
          style={{ backgroundColor: isSearching ? "#f1f5f9" : "transparent" }}
        >
          <span className="origins-panel-label">
            <SideLabel
              type="Koch Origins"
              onAdd={() => setIsAddingKochOrigin(true)}
            />
          </span>

          {isAddingKochOrigin && kochOrigins.length > 0 && (
            <div className="vertical-divider" />
          )}

          {isAddingKochOrigin && (
            <AddOriginColumn
              onAdd={(origin?: SelectedOrigin) =>
                handleAddOrigin("koch", origin)
              }
              onCancel={() => setIsAddingKochOrigin(false)}
              favoriteOrigins={kochOrigins}
              isCompetitor={false}
            />
          )}

          {isLoadingNewKochOrigin && (
            <>
              <div className="vertical-divider" />
              <div className="origin-column loading-column">
                <CircularProgress size={24} />
                <span>Adding...</span>
              </div>
            </>
          )}

          {/* display previous button if there are more origins to show */}
          {kochOriginIndex > 0 && (
            <Button
              className="arrow-btn"
              onClick={() => setKochOriginIndex(kochOriginIndex - 1)}
            >
              <img
                data-tooltip-id="koch-before-button-icon"
                src={icons.BeforeButtonIcon}
                alt="BeforeButtonIcon"
              />
            </Button>
          )}

          {/* display favorite koch origins */}
          {visibleKochOrigins.map((origin, index) => {
            const uniqueMenuId = `koch-${origin.location.id}-${index}`;
            const uniqueOriginId = `koch-${product}-${origin.location.id}-${index}`;
            return (
              <React.Fragment key={uniqueOriginId}>
                <div className="vertical-divider" />
                <OriginColumn
                  origin={origin}
                  user={user}
                  isMenuOpen={openMenuId === uniqueMenuId}
                  onMenuToggle={(event: any) => {
                    event.stopPropagation();
                    setOpenMenuId(
                      openMenuId === uniqueMenuId ? null : uniqueMenuId,
                    );
                  }}
                  type="koch"
                  onCloseMenu={() => setOpenMenuId(null)}
                  onRemove={handleRemoveOrigin}
                />
              </React.Fragment>
            );
          })}

          {/* display next button if there are more origins to show */}
          {kochOrigins.length > KOCH_ORIGINS_LIMIT &&
            kochOriginIndex < kochOrigins.length - KOCH_ORIGINS_LIMIT && (
              <>
                <div className="vertical-divider" />
                <Button
                  className="arrow-btn"
                  onClick={() => setKochOriginIndex(kochOriginIndex + 1)}
                >
                  <img
                    data-tooltip-id="next-button-icon-koch"
                    src={icons.NextButtonIcon}
                    alt="NextButtonIcon"
                  />
                </Button>
              </>
            )}
        </div>

        <div className="vertical-divider" />

        <div
          className="competitors-panel"
          style={{ backgroundColor: isSearching ? "#f1f5f9" : "transparent" }}
        >
          <SideLabel
            type="Competitor Origins"
            onAdd={() => setIsAddingCompetitorOrigin(true)}
          />
          {isAddingCompetitorOrigin && competitorOrigins.length > 0 && (
            <div className="vertical-divider" />
          )}
          {isAddingCompetitorOrigin && (
            <AddOriginColumn
              onAdd={(origin?: SelectedOrigin) =>
                handleAddOrigin("competitor", origin)
              }
              onCancel={() => setIsAddingCompetitorOrigin(false)}
              favoriteOrigins={competitorOrigins}
              isCompetitor
            />
          )}

          {/* Show loading spinner for new competitor origin */}
          {isLoadingNewCompetitorOrigin && (
            <>
              <div className="vertical-divider" />
              <div className="origin-column loading-column">
                <CircularProgress size={24} />
                <span>Adding...</span>
              </div>
            </>
          )}
          {/* display previous button if there are more origins to show */}
          {competitorOriginIndex > 0 && (
            <Button
              className="arrow-btn"
              onClick={() =>
                setCompetitorOriginIndex(competitorOriginIndex - 1)
              }
            >
              <img
                data-tooltip-id="competitor-before-button-icon"
                src={icons.BeforeButtonIcon}
                alt="BeforeButtonIcon"
              />
            </Button>
          )}
          {/* display favorite competitor origins */}
          {visibleCompetitorOrigins.map((origin, index) => {
            const uniqueMenuId = `competitor-${origin.location.id}-${index}`;
            const uniqueOriginId = `koch-${product}-${origin.location.id}-${index}`;
            return (
              <React.Fragment key={uniqueOriginId}>
                <div className="vertical-divider" />
                <OriginColumn
                  key={uniqueMenuId}
                  origin={origin}
                  user={user}
                  isMenuOpen={openMenuId === uniqueMenuId}
                  onMenuToggle={(event: any) => {
                    event.stopPropagation();
                    setOpenMenuId(
                      openMenuId === uniqueMenuId ? null : uniqueMenuId,
                    );
                  }}
                  type="competitor"
                  onCloseMenu={() => setOpenMenuId(null)}
                  onRemove={handleRemoveOrigin}
                />
              </React.Fragment>
            );
          })}
          {/* display next button if there are more origins to show */}
          {competitorOrigins.length > COMPETITOR_ORIGINS_LIMIT &&
            competitorOriginIndex <
              competitorOrigins.length - COMPETITOR_ORIGINS_LIMIT && (
              <>
                <div className="vertical-divider" />
                <Button
                  className="arrow-btn"
                  onClick={() =>
                    setCompetitorOriginIndex(competitorOriginIndex + 1)
                  }
                >
                  <img
                    data-tooltip-id="next-button-icon"
                    src={icons.NextButtonIcon}
                    alt="NextButtonIcon"
                  />
                </Button>
              </>
            )}
        </div>
      </div>
      <div className="header-panel-spacer" />
    </div>
  );
};

export default HeaderPanel;
